import React, { useEffect, useState, useRef, useCallback } from "react";
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import axios from 'axios'
import { BASEURL } from "../Config"
import Footer from "../components/Footer";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import swal from 'sweetalert';
import CustomPagination from "../components/common/CustomPagination";
import ApplyLeaveModal from "../components/common/ApplyLeaveModal";
import moment from 'moment';
import '../assets/leave-requests.css';
import '../assets/enhanced-tracking.css';

const LEAVE_TYPES = [
    { label: 'Leave', value: 'leave' },
    { label: 'Holiday', value: 'holiday' }
];

const SALES_MANAGER_PAGE_SIZE = 20;

const LeaveRequests = () => {
    const [activeTab, setActiveTab] = useState('pending');
    const [currentPage, setCurrentPage] = useState(0);
    const [offset, setOffset] = useState(0);
    const [slice, setSlice] = useState([]);
    const [perPage, setPerPage] = useState(10);
    const [pageCount, setPageCount] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [noResults, setNoResults] = useState(false);
    const [selectedLeaveType, setSelectedLeaveType] = useState(null);
    const [selectedSalesManager, setSelectedSalesManager] = useState(null);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [showFilterDropdown, setShowFilterDropdown] = useState(false);
    const filterRef = useRef(null);

    // Sales manager dropdown state for pagination
    const [salesManagerOptions, setSalesManagerOptions] = useState([]);
    const [salesManagerTotal, setSalesManagerTotal] = useState(0);
    const [salesManagerOffset, setSalesManagerOffset] = useState(0);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    // Apply Leave Modal state
    const [showApplyModal, setShowApplyModal] = useState(false);

    // Load initial sales managers
    useEffect(() => {
        loadSalesManagers('', 0, true);
    }, []);

    const loadSalesManagers = async (search = '', offset = 0, reset = false) => {
        try {
            const res = await axios.get(`${BASEURL}/api/v1/salesManager?offset=${offset}&limit=${SALES_MANAGER_PAGE_SIZE}&fields=firstName,lastName,uuid${search ? `&search=${search}` : ''}`);
            if (res.status === 200) {
                const newOptions = res.data.salesManagers.map(sm => ({
                    label: `${sm.firstName} ${sm.lastName}`,
                    value: sm.uuid
                }));
                if (reset) {
                    setSalesManagerOptions(newOptions);
                } else {
                    setSalesManagerOptions(prev => [...prev, ...newOptions]);
                }
                setSalesManagerTotal(res.data.total);
                setSalesManagerOffset(offset + SALES_MANAGER_PAGE_SIZE);
            }
        } catch (err) {
            console.log('Error fetching sales managers', err);
        }
    };

    const loadMoreSalesManagers = useCallback(() => {
        if (isLoadingMore || salesManagerOptions.length >= salesManagerTotal) return;
        setIsLoadingMore(true);
        loadSalesManagers('', salesManagerOffset, false).finally(() => setIsLoadingMore(false));
    }, [salesManagerOffset, salesManagerTotal, salesManagerOptions.length, isLoadingMore]);

    const handleSalesManagerSearch = useCallback(async (inputValue) => {
        if (!inputValue) {
            loadSalesManagers('', 0, true);
            return salesManagerOptions;
        }
        try {
            const res = await axios.get(`${BASEURL}/api/v1/salesManager?offset=0&limit=${SALES_MANAGER_PAGE_SIZE}&fields=firstName,lastName,uuid&search=${inputValue}`);
            if (res.status === 200) {
                const options = res.data.salesManagers.map(sm => ({
                    label: `${sm.firstName} ${sm.lastName}`,
                    value: sm.uuid
                }));
                return options;
            }
            return [];
        } catch (err) {
            console.log('Error searching sales managers', err);
            return [];
        }
    }, [salesManagerOptions]);

    useEffect(() => {
        fetchLeaveRequests();
    }, [offset, perPage, activeTab]);

    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            if (searchTerm !== undefined) {
                setOffset(0);
                setCurrentPage(0);
                fetchLeaveRequests();
            }
        }, 500);

        return () => clearTimeout(debounceTimer);
    }, [searchTerm]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (filterRef.current && !filterRef.current.contains(event.target)) {
                setShowFilterDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const fetchLeaveRequests = () => {
        let url = `${BASEURL}/api/v1/salesManagerAttendance/leaveRequest?offset=${offset}&limit=${perPage}`;

        if (activeTab === 'pending') {
            url += `&status=pending`;
        }

        if (searchTerm) {
            url += `&search=${searchTerm}`;
        }
        if (selectedLeaveType) {
            url += `&leaveType=${selectedLeaveType.value}`;
        }
        if (selectedSalesManager) {
            url += `&salesManagerId=${selectedSalesManager.value}`;
        }
        if (startDate) {
            url += `&startDate=${startDate}`;
        }
        if (endDate) {
            url += `&endDate=${endDate}`;
        }

        axios.get(url)
            .then(res => {
                if (res.status === 200) {
                    setSlice(res.data.leaveRequests);
                    setPageCount(Math.ceil(res.data.total / perPage));
                    setTotalCount(res.data.total);
                    setNoResults(res.data.leaveRequests.length === 0);
                }
            })
            .catch((err) => {
                console.log(err.response?.data);
                swal('Error in Fetching Leave Requests', err.response?.data, 'error');
                console.log('Fetching Leave Requests Error', err);
            });
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setOffset(0);
        setCurrentPage(0);
    };

    const handlePerPageChange = (e) => {
        const newPerPage = parseInt(e.target.value);
        setPerPage(newPerPage);
        setOffset(0);
        setCurrentPage(0);
    };

    const handlePageClick = (e) => {
        const selectedPage = e.selected;
        const newOffset = selectedPage * perPage;
        setCurrentPage(selectedPage);
        setOffset(newOffset);
    };

    const handleApplyFilters = () => {
        setOffset(0);
        setCurrentPage(0);
        setShowFilterDropdown(false);
        fetchLeaveRequests();
    };

    const handleResetFilters = () => {
        setSelectedLeaveType(null);
        setSelectedSalesManager(null);
        setStartDate('');
        setEndDate('');
        setShowFilterDropdown(false);
        setOffset(0);
        setCurrentPage(0);
        let url = `${BASEURL}/api/v1/salesManagerAttendance/leaveRequest?offset=0&limit=${perPage}`;
        if (activeTab === 'pending') {
            url += `&status=pending`;
        }
        if (searchTerm) {
            url += `&search=${searchTerm}`;
        }
        axios.get(url)
            .then(res => {
                if (res.status === 200) {
                    setSlice(res.data.leaveRequests);
                    setPageCount(Math.ceil(res.data.total / perPage));
                    setTotalCount(res.data.total);
                    setNoResults(res.data.leaveRequests.length === 0);
                }
            })
            .catch((err) => {
                console.log(err.response?.data);
                swal('Error in Fetching Leave Requests', err.response?.data, 'error');
            });
    };

    const getLeaveTypeBadgeClass = (type) => {
        switch (type) {
            case 'holiday':
                return 'badge-leave-type-holiday';
            case 'leave':
            default:
                return 'badge-leave-type-leave';
        }
    };

    const getLeaveStatusBadgeClass = (status) => {
        switch (status) {
            case 'approved':
                return 'badge-status-approved';
            case 'rejected':
                return 'badge-status-rejected';
            case 'pending':
            default:
                return 'badge-status-pending';
        }
    };

    const handleLeaveAction = (uuid, action) => {
        const actionText = action === 'approve' ? 'approve' : 'reject';
        const actionTitle = action === 'approve' ? 'Approve' : 'Reject';

        swal({
            title: `${actionTitle} Leave Request?`,
            text: `Are you sure you want to ${actionText} this leave request?`,
            icon: 'warning',
            buttons: ['Cancel', 'Yes'],
            dangerMode: action === 'reject',
        }).then((confirm) => {
            if (confirm) {
                axios.put(`${BASEURL}/api/v1/salesManagerAttendance/leaveAction/${uuid}`, { action })
                    .then(res => {
                        if (res.status === 200) {
                            swal('Success', `Leave request ${action}d successfully`, 'success');
                            fetchLeaveRequests();
                        }
                    })
                    .catch((err) => {
                        console.log(err.response?.data);
                        swal('Error', err.response?.data?.message || 'Failed to update status', 'error');
                    });
            }
        });
    };

    const hasActiveFilters = selectedLeaveType || selectedSalesManager || startDate || endDate;
    const activeFilterCount = [selectedLeaveType, selectedSalesManager, startDate, endDate].filter(Boolean).length;

    return (
        <div id="wrapper">
            <Sidebar act="leaveRequests" />
            <div className="content-page">
                <div className="content">
                    <Topbar act="Leave Requests" breadcrumbItems={['Sales App', 'Leave Requests']} />
                    <div className="sales-manager-container">

                        {/* Tabs */}
                        <div className="leave-tabs">
                            <button
                                onClick={() => handleTabChange('pending')}
                                className={`leave-tab-btn ${activeTab === 'pending' ? 'active' : ''}`}
                            >
                                Pending Requests
                            </button>
                            <button
                                onClick={() => handleTabChange('all')}
                                className={`leave-tab-btn ${activeTab === 'all' ? 'active' : ''}`}
                            >
                                All Requests
                            </button>
                        </div>

                        {/* Search and Filter Bar */}
                        <div className="leave-action-bar">
                            <div className="leave-search-box">
                                <i className="mdi mdi-magnify search-icon"></i>
                                <input
                                    type="text"
                                    className="form-control"
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Search by name or reason..."
                                    value={searchTerm}
                                />
                            </div>

                            {/* Action controls (Apply + Filter) */}
                            <div className="leave-action-controls">
                                {/* Apply Leave Button */}
                                <button
                                    className="btn-apply-leave"
                                    onClick={() => setShowApplyModal(true)}
                                >
                                    <i className="mdi mdi-plus"></i>
                                    Apply Leave / Holiday
                                </button>

                                {/* Filter Button with Dropdown */}
                                <div ref={filterRef} className="filter-container">
                                    <button
                                        className={`filter-btn ${hasActiveFilters ? 'has-filters' : ''}`}
                                        onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                                    >
                                        <i className="mdi mdi-filter-outline"></i>
                                        {hasActiveFilters && (
                                            <span className="filter-count-badge">{activeFilterCount}</span>
                                        )}
                                    </button>

                                    {/* Filter Dropdown */}
                                    {showFilterDropdown && (
                                        <div className="filter-dropdown">
                                            <div className="filter-dropdown-header">
                                                <h6>Filter Options</h6>
                                                <button className="filter-close-btn" onClick={() => setShowFilterDropdown(false)}>
                                                    <i className="mdi mdi-close"></i>
                                                </button>
                                            </div>
                                            <div className="filter-group">
                                                <label>Sales Manager</label>
                                                <AsyncSelect
                                                    value={selectedSalesManager}
                                                    onChange={setSelectedSalesManager}
                                                    loadOptions={handleSalesManagerSearch}
                                                    defaultOptions={salesManagerOptions}
                                                    onMenuScrollToBottom={loadMoreSalesManagers}
                                                    isClearable
                                                    placeholder="Search sales manager..."
                                                    menuPortalTarget={document.body}
                                                    menuPosition="fixed"
                                                    isLoading={isLoadingMore}
                                                    cacheOptions
                                                    styles={{
                                                        control: (base) => ({ ...base, minHeight: '38px' }),
                                                        menuPortal: (base) => ({ ...base, zIndex: 9999 })
                                                    }}
                                                />
                                            </div>
                                            <div className="filter-group">
                                                <label>Leave Type</label>
                                                <Select
                                                    value={selectedLeaveType}
                                                    onChange={setSelectedLeaveType}
                                                    options={LEAVE_TYPES}
                                                    isClearable
                                                    placeholder="Select..."
                                                    menuPortalTarget={document.body}
                                                    menuPosition="fixed"
                                                    styles={{
                                                        control: (base) => ({ ...base, minHeight: '38px' }),
                                                        menuPortal: (base) => ({ ...base, zIndex: 9999 })
                                                    }}
                                                />
                                            </div>
                                            <div className="filter-date-row">
                                                <div className="filter-group">
                                                    <label>From Date</label>
                                                    <input
                                                        type="date"
                                                        className="form-control"
                                                        value={startDate}
                                                        onChange={(e) => setStartDate(e.target.value)}
                                                    />
                                                </div>
                                                <div className="filter-group">
                                                    <label>To Date</label>
                                                    <input
                                                        type="date"
                                                        className="form-control"
                                                        value={endDate}
                                                        onChange={(e) => setEndDate(e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            <div className="filter-dropdown-footer">
                                                <button className="btn btn-light btn-sm btn-rounded" onClick={handleResetFilters}>
                                                    Reset
                                                </button>
                                                <button className="btn btn-primary btn-sm" onClick={handleApplyFilters}>
                                                    Apply
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Applied Filters Display */}
                        {hasActiveFilters && (
                            <div className="leave-applied-filters">
                                <span className="filters-label">Active Filters:</span>
                                {selectedSalesManager && (
                                    <span className="filter-chip">
                                        {selectedSalesManager.label}
                                        <button onClick={() => { setSelectedSalesManager(null); setTimeout(() => fetchLeaveRequests(), 0); }}>
                                            <i className="mdi mdi-close"></i>
                                        </button>
                                    </span>
                                )}
                                {selectedLeaveType && (
                                    <span className="filter-chip">
                                        {selectedLeaveType.label}
                                        <button onClick={() => { setSelectedLeaveType(null); setTimeout(() => fetchLeaveRequests(), 0); }}>
                                            <i className="mdi mdi-close"></i>
                                        </button>
                                    </span>
                                )}
                                {startDate && (
                                    <span className="filter-chip">
                                        From: {moment(startDate).format('DD MMM')}
                                        <button onClick={() => { setStartDate(''); setTimeout(() => fetchLeaveRequests(), 0); }}>
                                            <i className="mdi mdi-close"></i>
                                        </button>
                                    </span>
                                )}
                                {endDate && (
                                    <span className="filter-chip">
                                        To: {moment(endDate).format('DD MMM')}
                                        <button onClick={() => { setEndDate(''); setTimeout(() => fetchLeaveRequests(), 0); }}>
                                            <i className="mdi mdi-close"></i>
                                        </button>
                                    </span>
                                )}
                                <button className="clear-all-btn" onClick={handleResetFilters}>
                                    Clear All
                                </button>
                            </div>
                        )}

                        <div className="table-card">
                            <div style={{ overflowX: 'auto' }}>
                                <table className="table mb-0 leave-requests-table">
                                    <thead>
                                        <tr>
                                            <th>SNo</th>
                                            <th>Sales Manager</th>
                                            <th>Leave Date</th>
                                            <th>Leave Type</th>
                                            {activeTab === 'all' && <th>Status</th>}
                                            <th>Reason</th>
                                            <th>Applied On</th>
                                            {activeTab === 'pending' && <th className="text-center">Actions</th>}
                                        </tr>
                                    </thead>
                                    {noResults ? (
                                        <tbody>
                                            <tr>
                                                <td colSpan={activeTab === 'pending' ? 7 : 8}>
                                                    <div className="empty-state">
                                                        <i className="mdi mdi-file-document-outline"></i>
                                                        <p>No {activeTab === 'pending' ? 'pending' : ''} leave requests found</p>
                                                    </div>
                                                </td>
                                            </tr>
                                        </tbody>
                                    ) : (
                                        <tbody>
                                            {slice.map((data, idx) => (
                                                <tr key={data.uuid}>
                                                    <td>{currentPage * perPage + idx + 1}</td>
                                                    <td>
                                                        <div className="user-name-cell">
                                                            <div className="user-avatar">
                                                                <i className="mdi mdi-account" style={{ fontSize: '20px' }}></i>
                                                            </div>
                                                            <span className="user-name-text">
                                                                {data.salesManagerName}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td>{data.leaveDate ? moment(data.leaveDate).format('DD MMM YYYY') : '-'}</td>
                                                    <td>
                                                        <span className={getLeaveTypeBadgeClass(data.leaveType)}>
                                                            {data.leaveType ? data.leaveType.charAt(0).toUpperCase() + data.leaveType.slice(1) : '-'}
                                                        </span>
                                                    </td>
                                                    {activeTab === 'all' && (
                                                        <td>
                                                            <span className={getLeaveStatusBadgeClass(data.leaveStatus)}>
                                                                {data.leaveStatus ? data.leaveStatus.charAt(0).toUpperCase() + data.leaveStatus.slice(1) : 'Pending'}
                                                            </span>
                                                        </td>
                                                    )}
                                                    <td>
                                                        <span className="reason-text" title={data.leaveReason}>
                                                            {data.leaveReason || '-'}
                                                        </span>
                                                    </td>
                                                    <td>{data.createdAt ? moment(data.createdAt).format('DD MMM YYYY') : '-'}</td>
                                                    {activeTab === 'pending' && (
                                                        <td className="text-center">
                                                            <div className="leave-action-buttons">
                                                                <button
                                                                    className="btn-approve"
                                                                    onClick={() => handleLeaveAction(data.uuid, 'approve')}
                                                                    title="Approve"
                                                                >
                                                                    <i className="mdi mdi-check"></i>
                                                                    Approve
                                                                </button>
                                                                <button
                                                                    className="btn-reject"
                                                                    onClick={() => handleLeaveAction(data.uuid, 'reject')}
                                                                    title="Reject"
                                                                >
                                                                    <i className="mdi mdi-close"></i>
                                                                    Reject
                                                                </button>
                                                            </div>
                                                        </td>
                                                    )}
                                                </tr>
                                            ))}
                                        </tbody>
                                    )}
                                </table>
                            </div>

                            <CustomPagination
                                offset={offset}
                                perPage={perPage}
                                totalCount={totalCount}
                                pageCount={pageCount}
                                currentPage={currentPage}
                                onPageChange={handlePageClick}
                                onPerPageChange={handlePerPageChange}
                                sliceLength={slice.length}
                                itemLabel="leave requests"
                            />
                        </div>

                        {/* Apply Leave Modal */}
                        <ApplyLeaveModal
                            show={showApplyModal}
                            onClose={() => setShowApplyModal(false)}
                            onSuccess={fetchLeaveRequests}
                        />
                    </div>
                    
                    <Footer />
                </div>
            </div>
        </div>
    );
}

export default LeaveRequests;
